import React, { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { selectUserTimeFormat } from '@/redux/selectors/authSelectors';
import { utcToZonedTime } from 'date-fns-tz';
import { getYearMonthDay, makeDateInTZ } from '@/components/_common/SchemaForm/Widgets/DatePickerTimezoneWidget/utils';
import { format, getHours } from 'date-fns';
import styled from '@emotion/styled';

type TimeOption = {
  label: string;
  value: string; // ISO 8601 date
  disabled: boolean;
};

interface TimeInputProps {
  selectedTime: string; // ISO 8601 date
  timeInterval: number; // minutes
  min?: string; // ISO 8601 date
  max?: string; // ISO 8601 date
  timeZone: string;
  onTimeChange: (value: string) => void;
}

export const TimeInput: React.FC<TimeInputProps> = ({ selectedTime, timeInterval, timeZone, min, max, onTimeChange }) => {
  const timeFormat = useAppSelector(selectUserTimeFormat);
  const selectedItemRef = useRef<HTMLLIElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (listRef.current && selectedItemRef.current) {
      const selectedItemRect = selectedItemRef.current.getBoundingClientRect();
      const containerRect = listRef.current.getBoundingClientRect();
      listRef.current.scrollTo({
        top: selectedItemRect.top - containerRect.top - selectedItemRect.height * 1.5
      });
    }
  }, []);

  const timesList: TimeOption[] = useMemo(() => {
    const startTimeZoned = utcToZonedTime(selectedTime ? selectedTime : min ? min : Date.now(), timeZone);
    const [year, month, day] = getYearMonthDay(startTimeZoned);
    const startTime = makeDateInTZ(timeZone, year, month, day).valueOf();

    const maxHoursPerDay = 25; // day may have 25 hours if it's the days when clock is changed one 1 hour back
    const slotsCount = (maxHoursPerDay * 60) / timeInterval;

    let dayDurationInHours: number | undefined;

    return Array.from({ length: slotsCount }).flatMap((_, i) => {
      const time = new Date(startTime + timeInterval * 60_000 * i);
      const timeZoned = utcToZonedTime(time, timeZone);
      const hours = getHours(timeZoned);
      const timeFormatted = format(timeZoned, timeFormat);

      // Find out how many hours in this day
      if (i * timeInterval === 23 * 60) {
        if (hours === 23) {
          dayDurationInHours = 24;
        } else if (hours === 22) {
          dayDurationInHours = 25;
        } else {
          dayDurationInHours = 23;
        }
      }

      if (dayDurationInHours && i * timeInterval >= dayDurationInHours * 60) {
        // stop producing items if we already have all slots up to 00:00 time
        return [];
      }

      return [
        {
          label: timeFormatted,
          value: time.toISOString(),
          disabled: (min ? time <= new Date(min) : false) || (max ? time >= new Date(max) : false)
        }
      ];
    });
  }, [selectedTime, timeZone, timeFormat, min, max]);

  return (
    <Wrapper className="react-datepicker__time-container">
      <div className="react-datepicker__header react-datepicker__header--time">
        <div className="react-datepicker-time__header">Time</div>
      </div>
      <div className="react-datepicker__time">
        <div className="react-datepicker__time-box">
          <ul ref={listRef} className="react-datepicker__time-list" role="listbox" aria-label="Time">
            {timesList.map((timeOption) => {
              const isSelected = timeOption.value === selectedTime;

              const classNames = ['react-datepicker__time-list-item'];
              if (isSelected) classNames.push('react-datepicker__time-list-item--selected');
              if (timeOption.disabled) classNames.push('react-datepicker__time-list-item--disabled');

              const props = {
                className: classNames.join(' '),
                tabIndex: isSelected ? 0 : -1,
                role: 'option',
                'aria-selected': isSelected
              };

              // TODO: add keyboard navigation
              return (
                <li
                  ref={isSelected ? selectedItemRef : undefined}
                  key={timeOption.value}
                  {...props}
                  onClick={() => {
                    if (!timeOption.disabled) onTimeChange(timeOption.value);
                  }}
                >
                  {timeOption.label}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  & .react-datepicker__time-list-item--disabled {
    opacity: 0.2;
  }
`;
